import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { applyTheme } from "@/lib/theme";
import { resolvePortalKeyFromHost } from "@/lib/portal-domain";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Loader } from "@/components/portal/ui";
import { AuthShell, AuthLabel, AuthInput, AuthPrimaryButton, AUTH } from "@/components/portal/AuthShell";

const HERO = "https://images.unsplash.com/photo-1649065150177-a336543e9814?w=1500&q=80";

// Central Control OS entry (app.kewagportal.ch / route "/"). Team login only —
// no open mass registration. Self-registration lives on the branded portal apps.
export default function AppEntry() {
  const { user, loading, login } = useAuth();
  const { t, L } = useI18n();
  const navigate = useNavigate();
  const [portals, setPortals] = useState(null);
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/portals/_control/config").then((r) => applyTheme(r.data.theme)).catch(() => {});
    api.get("/portals").then(({ data }) => setPortals(data)).catch(() => setPortals([]));
  }, []);

  if (loading || portals === null) return <div className="min-h-screen flex items-center justify-center" style={{ background: AUTH.bg }}><Loader /></div>;

  // Domain-based tenant: app.<brand>.ch shows that brand's login instead of Control OS.
  const hostPortal = resolvePortalKeyFromHost(portals);
  if (hostPortal) return <Navigate to={`/p/${hostPortal}/auth`} replace />;
  if (user) return <Navigate to="/select" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(form.email, form.password);
      navigate("/select");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const forgot = () => toast.info(L({ de: "Bitte wende dich an das KEWAG-Team.", fr: "Veuillez contacter l'équipe KEWAG.", it: "Contatta il team KEWAG.", en: "Please contact the KEWAG team." }));

  const heroTitle = (<>Ein Core.<br />Viele Marken.<br /><span style={{ color: "var(--accent)" }}>Ein Control OS.</span></>);

  return (
    <AuthShell
      brandName="KEWAGPortal"
      brandTag="Control OS"
      heroKicker="KEWAGPortal Control OS"
      heroTitle={heroTitle}
      heroSubline={L({ de: "Portal-Apps, Anbieter, Anfragen, Pakete, Freigaben und Rollen zentral steuern.", fr: "Pilotez apps de portail, prestataires, demandes, forfaits, validations et rôles au même endroit.", it: "Gestisci app di portale, fornitori, richieste, pacchetti, approvazioni e ruoli in un unico posto.", en: "Manage portal apps, providers, inquiries, packages, approvals and roles in one place." })}
      chips={(portals || []).map((p) => p.name)}
      heroImage={HERO}
    >
      <span className="inline-flex items-center text-[11px] font-semibold uppercase tracking-[0.18em] px-3 py-1 rounded-full mb-4" style={{ border: "1px solid var(--accent)", color: "var(--accent)" }}>
        {t("auth.teamAccess")}
      </span>
      <h2 className="font-heading text-3xl font-bold" style={{ color: AUTH.text }}>{t("auth.welcome")}</h2>
      <p className="mt-1.5 text-sm" style={{ color: AUTH.muted }}>
        {L({ de: "Zugang für das KEWAG-Team. Portal-Konten laufen über die jeweilige Marken-App.", fr: "Accès pour l'équipe KEWAG. Les comptes de portail passent par l'app de la marque.", it: "Accesso per il team KEWAG. Gli account di portale passano dall'app del brand.", en: "Access for the KEWAG team. Portal accounts live on each brand app." })}
      </p>

      <form onSubmit={submit} className="mt-6 space-y-4">
        <div>
          <AuthLabel>{t("auth.email")}</AuthLabel>
          <AuthInput data-testid="entry-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="name@kewagportal.ch" />
        </div>
        <div>
          <AuthLabel>{t("auth.password")}</AuthLabel>
          <AuthInput data-testid="entry-password" required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" />
        </div>
        <AuthPrimaryButton type="submit" disabled={busy} data-testid="entry-submit">{t("auth.signIn")}</AuthPrimaryButton>
      </form>

      <div className="mt-3 text-right">
        <button type="button" onClick={forgot} className="text-xs font-medium" style={{ color: "var(--accent)" }} data-testid="entry-forgot">{t("auth.forgotPassword")}</button>
      </div>

      <p className="mt-6 text-center text-xs" style={{ color: AUTH.muted }}>{t("auth.loginTrouble")}</p>
    </AuthShell>
  );
}
